// src/pages/DealersPage.tsx — Admin Dealer Management (list / search / add / edit)
import React, { useState, useEffect, useCallback } from 'react';
import { getDealers, addDealer, updateDealer } from '../services/api';
import type { Dealer } from '../types';
import { apiError } from '../utils/errors';

/** Editable dealer fields shown in the add/edit modal. */
type DealerForm = Partial<Dealer> & Pick<Dealer, 'dealerCode' | 'name' | 'mobile' | 'districtCode' | 'state'>;
type DealerField = 'dealerCode' | 'name' | 'mobile' | 'districtCode' | 'state' | 'eUrvarakCode';
type Modal =
  | { mode: 'add';  data: DealerForm }
  | { mode: 'edit'; data: Dealer };

const EMPTY: DealerForm = { dealerCode:'', name:'', mobile:'', districtCode:'', state:'', eUrvarakCode:'' };

export default function DealersPage() {
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [search,  setSearch]  = useState('');
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState<Modal | null>(null);   // null | {mode:'add'|'edit', data}
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getDealers({ search });
      setDealers(r.data.data || []);
    } catch { setDealers([]); }
    finally { setLoading(false); }
  }, [search]);

  // debounce search a little
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openAdd  = ()  => { setErr(''); setModal({ mode:'add',  data:{ ...EMPTY } }); };
  const openEdit = (d: Dealer) => { setErr(''); setModal({ mode:'edit', data:{ ...d } }); };

  const save = async () => {
    if (!modal) return;
    setSaving(true); setErr('');
    try {
      if (modal.mode === 'add') {
        const d = modal.data;
        if (!d.dealerCode || !d.name) { setErr('Dealer code and name are required'); setSaving(false); return; }
        await addDealer(d);
      } else {
        const d = modal.data;
        await updateDealer(d.id, {
          name:d.name, mobile:d.mobile, districtCode:d.districtCode,
          state:d.state, eUrvarakCode:d.eUrvarakCode,
        });
      }
      setModal(null);
      load();
    } catch (e) {
      setErr(apiError(e, 'Could not save dealer'));
    } finally { setSaving(false); }
  };

  const isPlaceholder = (d: Dealer) => /^Dealer DLR-/.test(d.name || '');
  const field = (k: DealerField, v: string) => setModal(m => {
    if (!m) return m;
    // branch on mode so each spread keeps its own `data` type
    return m.mode === 'add'
      ? { ...m, data:{ ...m.data, [k]:v } }
      : { ...m, data:{ ...m.data, [k]:v } };
  });

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">🏬 Dealer Management</div>
        <span style={{ fontSize:12, color:'var(--ink3)' }}>{dealers.length} dealers</span>
        <button className="btn btn-primary" style={{ padding:'7px 14px', fontSize:12 }} onClick={openAdd}>+ Add Dealer</button>
      </div>

      <div className="page-content fade-in">
        <input
          style={{ width:'100%', maxWidth:420, padding:'9px 14px', border:'1.5px solid var(--bor)', borderRadius:9, fontSize:13, outline:'none', marginBottom:16 }}
          placeholder="Search by code, name or district…"
          value={search} onChange={e => setSearch(e.target.value)}
        />

        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr><th>Code</th><th>Name</th><th>Mobile</th><th>District</th><th>State</th><th>Action</th></tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ textAlign:'center', padding:32 }}><span className="spin">⟳</span></td></tr>
                ) : dealers.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign:'center', padding:32, color:'var(--ink3)' }}>No dealers found</td></tr>
                ) : dealers.map(d => (
                  <tr key={d.id}>
                    <td className="mono" style={{ fontSize:11 }}>{d.dealerCode}</td>
                    <td style={{ fontSize:12 }}>
                      {d.name}
                      {isPlaceholder(d) && <span className="tag tag-amber" style={{ marginLeft:6, fontSize:9 }}>auto</span>}
                    </td>
                    <td className="mono" style={{ fontSize:11, color:'var(--ink3)' }}>{d.mobile}</td>
                    <td style={{ fontSize:11 }}>{d.districtCode}</td>
                    <td style={{ fontSize:11, color:'var(--ink3)' }}>{d.state}</td>
                    <td><button className="btn btn-secondary" style={{ padding:'4px 12px', fontSize:11 }} onClick={() => openEdit(d)}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div style={{ fontSize:11, color:'var(--ink3)', marginTop:10 }}>
          Dealers tagged <span className="tag tag-amber" style={{ fontSize:9 }}>auto</span> were created automatically during an Excel import — edit them to add real name & contact.
        </div>
      </div>

      {/* Add / Edit modal */}
      {modal && (
        <div onClick={() => !saving && setModal(null)}
             style={{ position:'fixed', inset:0, background:'rgba(7,46,32,0.45)', backdropFilter:'blur(2px)', display:'flex', alignItems:'center', justifyContent:'center', padding:16, zIndex:300 }}>
          <div onClick={e => e.stopPropagation()} className="fade-in"
               style={{ width:'100%', maxWidth:440, background:'#fff', borderRadius:16, padding:24, boxShadow:'var(--shadow-lg)' }}>
            <h3 style={{ marginBottom:4 }}>{modal.mode === 'add' ? 'Add Dealer' : 'Edit Dealer'}</h3>
            <p style={{ fontSize:12, color:'var(--ink3)', marginBottom:16 }}>
              {modal.mode === 'edit' ? `Code: ${modal.data.dealerCode} (cannot be changed)` : 'Fill in the dealer details'}
            </p>

            {err && <div className="alert alert-danger" style={{ marginBottom:14 }}>{err}</div>}

            {modal.mode === 'add' && (
              <div className="field">
                <label>Dealer Code <span className="req">*</span></label>
                <input value={modal.data.dealerCode} onChange={e => field('dealerCode', e.target.value)} placeholder="DLR-MH-001" />
              </div>
            )}
            <div className="field">
              <label>Name <span className="req">*</span></label>
              <input value={modal.data.name} onChange={e => field('name', e.target.value)} placeholder="Krishi Bhandar Nashik" />
            </div>
            <div className="r-grid-2" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div className="field">
                <label>Mobile</label>
                <input value={modal.data.mobile} onChange={e => field('mobile', e.target.value)} placeholder="9812341234" />
              </div>
              <div className="field">
                <label>District Code</label>
                <input value={modal.data.districtCode} onChange={e => field('districtCode', e.target.value)} placeholder="MH-NAS" />
              </div>
            </div>
            <div className="r-grid-2" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div className="field">
                <label>State</label>
                <input value={modal.data.state} onChange={e => field('state', e.target.value)} placeholder="Maharashtra" />
              </div>
              <div className="field">
                <label>e-Urvarak Code</label>
                <input value={modal.data.eUrvarakCode || ''} onChange={e => field('eUrvarakCode', e.target.value)} placeholder="optional" />
              </div>
            </div>

            <div style={{ display:'flex', gap:10, marginTop:8 }}>
              <button className="btn btn-secondary" style={{ flex:1, justifyContent:'center' }} onClick={() => setModal(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" style={{ flex:1, justifyContent:'center' }} onClick={save} disabled={saving}>
                {saving ? <span className="spin">⟳</span> : (modal.mode === 'add' ? 'Add Dealer' : 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
